import * as React from 'react';
import { useIntl } from 'react-intl';
import dayjs from 'dayjs';
import { useRecoilValue } from 'recoil';
import {
  CmdInput,
  CmdLabel,
  CmdModal,
  CmdModalFooterButton,
  CmdSimpleSelect,
} from '@commander-services/gui-components';
import { Controller, useForm } from 'react-hook-form';
import { useQueries } from '@tanstack/react-query';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { AxiosResponse } from 'axios';
import FormikValidatorService, {
  IInput,
  IValidationApiResponse,
} from '../../Services/FormikValidatorService';
import PermissionsService, {
  PERMISSION_PERIOD_LOCK_ADMIN,
  PERMISSION_TACHO_CORRECTION,
  PERMISSION_TACHO_CORRECTION_ADMIN,
  PERMISSION_TACHO_CORRECTION_NO_LIMIT,
} from '../../Services/PermissionsService';
import configJs from '../../../config';
import TableService, { TABLE_CORRECTIONS } from '../../Services/TableService';
import usePageWithForm from '../../hooks/usePageWithForm';
import ConfirmDialog from '../ConfirmDialog';
import { IVehicle, IVehicles } from '../Vehicles/types';
import { LOCKED_KEY, RESTRICTED_KEY, UNLOCKED_KEY } from '../TripsOverviewNew/constants';
import CmdUploadHookForm from '../Forms/CmdUploadHookForm';
import { handleFormDataCorrection } from './CorrectionService';
import Calendar from '../Forms/ReactHookFormFields/Calendar';
import SimpleSelectWithSearchHookForm from '../Forms/Fields/SimpleSelectWithSearchHookForm';
import { transformErrors, validateFields } from '../../Services/HookFormService';
import { selectedCustomersAtom } from '../../store/recoil/customers';
import { selectedVehiclesAtom, vehiclesAtom } from '../../store/recoil/vehicles';
import { postRequest, putRequest } from '../../Services/HttpService';
import useAnalytics from '../../hooks/useAnalytics';

dayjs.extend(utc);
dayjs.extend(timezone);

const RESPONSE_OK = 'ok';

interface ModalFormProps {
  handleClose: any;
  id: number | null;
  customerId: number | null;
  updateTable: any;
  locked?: string;
}

type TCorrectionTypeForm =
  // | 'existingCorrection'
  | 'timestamp'
  | 'vehicleId'
  | 'type'
  | 'lastAffectedRideId'
  | 'mileageOrig'
  | 'mileage'
  | 'lockedStatus'
  | 'attachments';

interface ICorrectionFormValues {
  timestamp: Date;
  vehicleId?: string;
  type: string;
  lastAffectedRideId: string;
  mileageOrig: string;
  mileage: string;
  lockedStatus: string;
  attachments: {
    id: number;
    customerId: number;
    fileName: string;
    serverPath: string;
    mimeType: string;
    fileSize: number;
    createdBy: number;
    createdAt: string | null;
    modifiedBy: string | null;
    modifiedAt: string | null;
    deletedBy: string | null;
    deletedAt: string | null;
    temporary: boolean;
  }[];
}
interface ILastAffectedRideIdOption {
  item: string;
  value: string;
}

export default function Form(props: ModalFormProps): JSX.Element {
  usePageWithForm();
  const selectedCustomers = useRecoilValue<number[]>(selectedCustomersAtom);
  const [isLoaderActive, setIsLoaderActive] = React.useState<boolean>(false);
  const [infoMessage, setInfoMessage] = React.useState('');
  const [isConfirmModalOpened, setIsConfirmModalOpened] = React.useState<boolean>(false);
  const allVehicles = useRecoilValue<IVehicles>(vehiclesAtom);
  const selectedVehicles = useRecoilValue<number[]>(selectedVehiclesAtom);
  const { formatMessage: f } = useIntl();
  const { id } = props;
  const [validation, setValidation] = React.useState<IValidationApiResponse | null>(null);
  const [lastAffectedRideIdOptions, setLastAffectedRideIdOptions] = React.useState<
    ILastAffectedRideIdOption[]
  >([]);
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { errors },
  } = useForm<ICorrectionFormValues>({
    mode: 'onChange',
    defaultValues: {
      // existingCorrection: '',
      timestamp: undefined,
      vehicleId:
        selectedVehicles.length === 1 && !props.id ? String(selectedVehicles[0]) : undefined,
      type: '',
      lastAffectedRideId: '',
      mileageOrig: '',
      mileage: '',
      lockedStatus: '',
      attachments: [],
    },
    resolver: async (formData) => {
      const milageRules =
        validation?.mileage &&
        validation?.mileage &&
        !PermissionsService.hasAccess(
          PERMISSION_TACHO_CORRECTION_NO_LIMIT,
          [props.id ? 'update' : 'create'],
          selectedCustomers
        )
          ? validation.mileage
          : [
              {
                param: 'required',
                value: true,
                message: f({ id: 'form.required' }),
              },
              {
                param: 'min',
                value: 0.1,
                message: f({ id: 'form.rules.minValue' }, { value: 0.1 }),
              },
              {
                param: 'max',
                value: 999999.9,
                message: f({ id: 'form.rules.maxValue' }, { value: 999999.9 }),
              },
            ];

      const validateErros = validateFields(formData, {
        ...validation,
        mileage: milageRules,
      });
      const formErrors = transformErrors(validateErros);
      return {
        values: formErrors && Object.keys(formErrors).length ? {} : formData, // if there's an error, return empty values
        errors: formErrors,
      };
    },
  });

  // create
  const create = async (values) => {
    if (Object.keys(errors).length > 0) return;
    setIsLoaderActive(true);
    const newValues = {
      ...values,
      mileage: Number(values.mileage),
      mileageOrig: Number(values.mileageOrig),
      customerId: allVehicles[values.vehicleId].customerId,
    };
    if (infoMessage) {
      newValues.noLimit = true;
    }
    delete newValues.lockedStatus;
    delete newValues.type;
    const response: AxiosResponse | false = await postRequest('/v1/tacho-correction/add', newValues, { returnAxiosResponse: true });
    setIsLoaderActive(false);
    if (response && response.data && response.status === 200) {
      props.handleClose();
      props.updateTable();
    }
  };

  // update
  const update = async (values) => {
    if (Object.keys(errors).length > 0) return;
    const newValues = {
      ...values,
      mileage: Number(values.mileage),
      mileageOrig: Number(values.mileageOrig),
      customerId: props.customerId,
    };

    setIsLoaderActive(true);
    if (props.id) {
      delete newValues.vehicleId;
      // delete newValues.lockedStatus;
      // delete newValues.type;
    }
    if (infoMessage) {
      newValues.noLimit = true;
    }
    delete newValues.lockedStatus;
    delete newValues.type;
    const response: AxiosResponse | false = await putRequest(
      `/v1/tacho-correction/${id}/edit${`?customerId=${props.customerId}`}`,
      newValues,
      { returnAxiosResponse: true }
    );
    setIsLoaderActive(false);
    if (response && response.data && response.data.status === RESPONSE_OK) {
      props.handleClose();
      props.updateTable();
    }
  };

  const getLockedRideBoolean = (locked: string) => {
    if (
      locked === UNLOCKED_KEY &&
      !PermissionsService.hasAccess(PERMISSION_TACHO_CORRECTION, ['update'], props.customerId)
    ) {
      return false;
    }

    if (
      locked === RESTRICTED_KEY &&
      !PermissionsService.hasAccess(PERMISSION_TACHO_CORRECTION_ADMIN, ['update'], props.customerId)
    ) {
      return false;
    }

    return (
      locked === LOCKED_KEY ||
      (locked === RESTRICTED_KEY &&
        !PermissionsService.hasAccess(PERMISSION_PERIOD_LOCK_ADMIN, ['update'], props.customerId))
    );
  };

  const handleInfoMessage = (value: number) => {
    setValue('mileage', String(value));
    trigger('mileage');
    const rules = validation?.mileage;
    if (
      rules &&
      value &&
      PermissionsService.hasAccess(
        PERMISSION_TACHO_CORRECTION_NO_LIMIT,
        [props.id ? 'update' : 'create'],
        selectedCustomers
      )
    ) {
      const minRule = rules.find((i) => i.param === 'min');
      const maxRule = rules.find((i) => i.param === 'max');
      if (minRule && value < minRule.value) {
        setInfoMessage(minRule.message);
      } else if (maxRule && value > maxRule.value) {
        setInfoMessage(maxRule.message);
      } else {
        setInfoMessage('');
      }
    }
  };

  // form effects
  const prepareSubmit = (values) => {
    if (Object.keys(errors).length === 0) {
      setIsConfirmModalOpened(false);
      if (id) {
        trackEvent(['corrections form', 'click', 'Edit correction']);
        update(values);
      } else {
        trackEvent(['corrections form', 'click', 'Add correction']);
        create(values);
      }
    }
  };

  const prepareNoLimitSubmit = async () => {
    if (Object.keys(errors).length === 0) {
      if (infoMessage) {
        setIsConfirmModalOpened(true);
      }
    }
  };

  // modal adding title and buttons
  let buttons: CmdModalFooterButton[] = [
    {
      id: 'modalClose',
      type: 'button',
      title: f({ id: 'form.close' }),
      className: 'e-button',
      closeCallback: props.handleClose,
      tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
    },
    {
      id: 'submitAddForm',
      type: 'submit',
      title: f({ id: 'form.save' }),
      className: 'e-button e-button--gray',
      submitCallback: handleSubmit(infoMessage ? prepareNoLimitSubmit : prepareSubmit),
      tooltip: f({ id: 'alarms.modal.recipient.save' }),
    },
  ];
  let isAllowed = true;

  // modal editing title and buttons
  if (id) {
    buttons = [
      {
        id: 'modalClose',
        type: 'button',
        title: f({ id: 'form.close' }),
        className: 'e-button',
        closeCallback: props.handleClose,
        tooltip: f({ id: 'alarms.modal.timeFrame.button.close.tooltip' }),
      },
    ];
    isAllowed =
      PermissionsService.hasAccess(PERMISSION_TACHO_CORRECTION, ['update'], props.customerId) &&
      !getLockedRideBoolean(props.locked || '');
    if (isAllowed) {
      buttons.push({
        id: 'submitEditForm',
        type: 'submit',
        title: f({ id: 'form.save' }),
        className: 'e-button e-button--gray',
        submitCallback: handleSubmit(infoMessage ? prepareNoLimitSubmit : prepareSubmit),
      });
    }
  }

  const resendDynamicForm = async () => {
    setIsLoaderActive(true);

    const newValues = { ...getValues() };
    if (props.id) {
      delete newValues.vehicleId;
    }

    let response;

    if (props.id) {
      if (!watch('vehicleId') && !watch('timestamp')) {
        response = await TableService.getForm(
          `${TABLE_CORRECTIONS}/${props.id}/form?customerId=${props.customerId}`
        );
      } else if (
        PermissionsService.hasAccess(PERMISSION_TACHO_CORRECTION, ['update'], props.customerId)
      ) {
        response = await TableService.resendDynamicFormById(
          TABLE_CORRECTIONS,
          props.id,
          newValues,
          props.customerId
        );
      }
    } else if (!watch('vehicleId') && !watch('timestamp')) {
      response = await TableService.getForm(`${TABLE_CORRECTIONS}/form`);
    } else {
      response = await TableService.resendDynamicForm(TABLE_CORRECTIONS, getValues());
    }

    if (response && response.form && response.form.inputs) {
      const inputs = response.form.inputs;

      setValidation(handleFormDataCorrection(inputs, 'rules'));

      setValue('mileage', '');
      setValue('mileageOrig', '');
      const filteredInputs = {
        existingCorrection: inputs.find((i: IInput) => i.indexId === 'existingCorrection'),
        timestamp: inputs.find((i: IInput) => i.indexId === 'timestamp'),
        vehicleId: inputs.find((i: IInput) => i.indexId === 'vehicleId'),
        type: inputs.find((i: IInput) => i.indexId === 'type'),
        lastAffectedRideId: inputs.find((i: IInput) => i.indexId === 'lastAffectedRideId'),
        mileageOrig: inputs.find((i: IInput) => i.indexId === 'mileageOrig'),
        mileage: inputs.find((i: IInput) => i.indexId === 'mileage'),
        lockedStatus: inputs.find((i: IInput) => i.indexId === 'lockedStatus'),
        attachments: inputs.find((i: IInput) => i.indexId === 'attachments'),
      };

      const formatedLastAffectedRideIdOptions = filteredInputs.lastAffectedRideId.values.map(
        (item) => {
          const [datePart, ...rest] = item.item.split(' - ');
          const textPart = rest.join(' - ');

          const formattedDate = dayjs.utc(datePart).local().format('DD.MM.YYYY HH:mm');

          const newString = `${formattedDate} - ${textPart}`;

          return { ...item, item: newString };
        }
      );

      setLastAffectedRideIdOptions(formatedLastAffectedRideIdOptions);

      Object.keys(filteredInputs).forEach((fieldName) => {
        setValue(fieldName as TCorrectionTypeForm, filteredInputs[fieldName].value);
        // trigger(fieldName as TCorrectionTypeForm);
      });

      setIsLoaderActive(false);

      return filteredInputs;
    }

    setIsLoaderActive(false);
    return false;
  };

  const handleOnChangetimestamp = (value: Date) => {
    if (!props.id && watch('vehicleId')) {
      trackEvent(['corrections form', 'change', 'Timestamp change']);
      // getDateSpans();
      setValue('timestamp', new Date(value));
      setValue('mileage', '');
      resendDynamicForm();
    }
  };

  const results = useQueries({
    queries: [
      {
        queryKey: [
          'correctionFormData',
          props.id,
          watch('vehicleId'),
          watch('timestamp'),
          selectedVehicles.length === 1,
        ],
        queryFn: () => resendDynamicForm(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const { data: correctionDataForm, isLoading: isLoadingCorrectionData } = results[0];

  const handleVehicleIdChange = (value: string) => {
    trackEvent(['corrections form', 'change', 'Vehicle change']);
    setValue('vehicleId', value);

    if (!value) {
      setValue('lastAffectedRideId', '');
      setValue('mileageOrig', '');
    }

    setValue('mileage', '');
    setValue('attachments', []);
  };

  const handleLastAffectedRideIdChange = (value: string) => {
    trackEvent(['corrections form', 'change', 'Last affected ride change']);
    setValue('lastAffectedRideId', value);
    setValue('mileage', '');
    resendDynamicForm();
    trigger('lastAffectedRideId');
  };

  const closeModalForm = () => {
    setIsConfirmModalOpened(false);
    trackEvent(['corrections form', 'click', 'Close modal']);
  };

  const getMileageDiff = (value: number | undefined) => {
    const origValue =
      watch('vehicleId') && correctionDataForm ? correctionDataForm?.mileageOrig.value : 0;
    if (!value || !origValue) {
      return 0;
    }
    return (value - origValue).toFixed(2);
  };

  // Options for select
  const vehicleIdOptions =
    correctionDataForm && correctionDataForm?.vehicleId?.values?.length > 0
      ? correctionDataForm.vehicleId.values
      : Object.values(allVehicles)
          .filter(
            (vehicle: IVehicle) =>
              selectedVehicles.includes(vehicle.id) &&
              PermissionsService.hasAccess(
                PERMISSION_TACHO_CORRECTION,
                ['create'],
                vehicle?.customerId
              )
          )
          .map((vehicle) => {
            return {
              value: String(vehicle.id),
              item: `${vehicle.licenceNumber} ${vehicle.name}`,
            };
          });

  const isLoading = isLoadingCorrectionData || isLoaderActive;

  // form
  return (
    <>
      <CmdModal
        id="correction-form"
        title={props.id ? f({ id: 'table.detail' }) : f({ id: 'table.add' })}
        footerButtons={buttons}
        loader={isLoading}
        width="560px"
      >
        {isAllowed ? (
          <div className="new-css-wrapper">
            {id ? (
              <CmdLabel
                id="timestamp"
                title={f({ id: 'table_list.columns.corrections.timestamp' })}
                value={dayjs(watch('timestamp')).format('D.M.YYYY H:mm')}
              />
            ) : (
              <Controller
                name="timestamp"
                control={control}
                render={({ field }) => (
                  <Calendar
                    {...field}
                    id="timestamp"
                    label={f({ id: 'table_list.columns.corrections.timestamp' })}
                    // dateFormat="d.m.Y H:i"
                    onChangeCallback={handleOnChangetimestamp}
                    afterToday={true}
                    required={FormikValidatorService.isFieldRequired(
                      validation ? validation.timestamp : []
                    )}
                    error={errors.timestamp?.message as string}
                    touched={true}
                  />
                )}
              />
            )}
            {id ? (
              <Controller
                name="vehicleId"
                control={control}
                render={({ field }) => (
                  <CmdLabel
                    {...field}
                    id="vehicleId"
                    title={f({ id: 'form.inputs.corrections.vehicle' })}
                  />
                )}
              />
            ) : (
              <Controller
                name="vehicleId"
                control={control}
                render={({ field }) => (
                  <SimpleSelectWithSearchHookForm
                    {...field}
                    id="vehicleId"
                    label={f({ id: 'form.inputs.corrections.vehicle' })}
                    introOption={f({ id: 'form.choose' })}
                    options={vehicleIdOptions}
                    onChangeCallback={handleVehicleIdChange}
                    required={FormikValidatorService.isFieldRequired(
                      validation ? validation.vehicleId : []
                    )}
                    control={control}
                    canRemoveValue={true}
                  />
                )}
              />
            )}
            <CmdLabel
              id="type"
              title={f({ id: 'form.inputs.corrections.correctionType' })}
              value={watch('type')}
            />
            <Controller
              name="lastAffectedRideId"
              control={control}
              render={({ field }) => (
                <CmdSimpleSelect
                  {...field}
                  id="lastAffectedRideId"
                  label={f({ id: 'form.inputs.corrections.lastAffectedRideId' })}
                  introOption={
                    !watch('vehicleId')
                      ? f({ id: 'form.placeholders.corrections.selectVehicleFirst' })
                      : f({ id: 'form.choose' })
                  }
                  options={lastAffectedRideIdOptions}
                  onChangeCallback={handleLastAffectedRideIdChange}
                  required={FormikValidatorService.isFieldRequired(
                    validation ? validation.lastAffectedRideId : []
                  )}
                  error={errors.lastAffectedRideId?.message}
                  touched={true}
                  withoutTruncate={true}
                />
              )}
            />

            <Controller
              name="mileageOrig"
              control={control}
              render={({ field }) => (
                <CmdLabel
                  {...field}
                  id="mileageOrig"
                  title={f({ id: 'form.inputs.corrections.mileageOrig' })}
                  value={watch('vehicleId') && watch('mileageOrig') ? watch('mileageOrig') : ''}
                />
              )}
            />

            <Controller
              name="mileage"
              control={control}
              render={({ field }) => (
                <CmdInput
                  {...field}
                  // name={fields[6].indexId}
                  id="mileage"
                  label={f({ id: 'form.inputs.corrections.mileage' })}
                  type="number"
                  // required={true}
                  infoMessage={infoMessage}
                  onChangeCallback={handleInfoMessage}
                  required={FormikValidatorService.isFieldRequired(
                    validation ? validation.mileage : []
                  )}
                  error={errors.mileage?.message}
                  touched={true}
                />
              )}
            />

            <Controller
              name="lockedStatus"
              control={control}
              render={({ field }) => (
                <CmdLabel
                  {...field}
                  title={f({ id: 'form.inputs.corrections.lockedStatus' })}
                  // value={correctionDataForm?.lockedStatus?.value || ''}
                />
              )}
            />

            {watch('vehicleId') ? (
              <Controller
                name="attachments"
                control={control}
                render={({ field }) => (
                  <CmdUploadHookForm
                    {...field}
                    id="attachments"
                    label={f({ id: 'form.inputs.attachments' })}
                    number={3}
                    maxFileSize={3}
                    acceptedFileTypes={['bmp', 'png', 'jpg', 'jpeg', 'gif', 'pdf']}
                    dnd={true}
                    url={`${configJs.Backend.url}/api/v1/file/upload${
                      props.customerId
                        ? `?customerId=${props.customerId}`
                        : `?customerId=${allVehicles[String(watch('vehicleId'))]?.customerId}`
                    }`}
                    control={control}
                    onChangeCallback={(value) => {
                      setValue('attachments', value);
                      trigger('attachments');
                      trackEvent(['corrections form', 'change', 'Attachments change']);
                    }}
                  />
                )}
              />
            ) : (
              <CmdLabel
                title={f({ id: 'form.inputs.attachments' })}
                value={f({ id: 'form.placeholders.corrections.selectVehicleFirst' })}
              />
            )}
          </div>
        ) : (
          <div className="form-horizontal">
            <CmdLabel
              title={f({ id: 'table_list.columns.corrections.timestamp' })}
              value={dayjs(watch('timestamp')).format('D.M.YYYY H:mm')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.corrections.vehicle' })}
              value={watch('vehicleId')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.corrections.correctionType' })}
              value={watch('type')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.corrections.lastAffectedRideId' })}
              value={
                lastAffectedRideIdOptions &&
                lastAffectedRideIdOptions.find((item) => item.value === watch('lastAffectedRideId'))
                  ? lastAffectedRideIdOptions.find(
                      (item) => item.value === watch('lastAffectedRideId')
                    )?.item
                  : undefined
              }
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.corrections.mileageOrig' })}
              value={watch('mileageOrig')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.corrections.mileage' })}
              value={watch('mileage')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.corrections.lockedStatus' })}
              value={watch('lockedStatus')}
              areLabels={true}
            />
            <CmdLabel
              title={f({ id: 'form.inputs.attachments' })}
              value={
                watch('attachments')
                  ? watch('attachments')
                      .map((item) => item.fileName)
                      .join(', ')
                  : undefined
              }
              areLabels={true}
            />
          </div>
        )}
      </CmdModal>
      {isConfirmModalOpened && (
        <ConfirmDialog
          title={f({ id: 'toastr.warning' })}
          cancelCallback={closeModalForm}
          continueCallback={handleSubmit(prepareSubmit)}
          cancelButtonTitle={f({ id: 'confirm.dialog.answerNo' })}
          continueButtonTitle={f({ id: 'confirm.dialog.answerYes' })}
        >
          {f(
            { id: 'confirm.dialog.corrections.noLimit' },
            {
              count: getMileageDiff(Number(watch('mileage'))),
            }
          )}
        </ConfirmDialog>
      )}
    </>
  );
}
